import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../../server";

const formatMoney = (amount, currency = "GBP") => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency || "GBP",
    }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
};

const AdminStripeCheckoutRecovery = () => {
  const [days, setDays] = useState(6);
  const [loading, setLoading] = useState(false);
  const [syncingBulk, setSyncingBulk] = useState(false);
  const [syncReport, setSyncReport] = useState(null);
  const [auditingMissingPi, setAuditingMissingPi] = useState(false);
  const [missingPiAudit, setMissingPiAudit] = useState(null);
  const [recoveringId, setRecoveringId] = useState("");
  const [primaryRows, setPrimaryRows] = useState([]);
  const [duplicateRows, setDuplicateRows] = useState([]);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [stats, setStats] = useState(null);
  const [scannedDays, setScannedDays] = useState(null);

  const loadOrphans = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${server}/order/list-orphaned-stripe-checkouts`,
        {
          params: { days, limit: 200 },
          withCredentials: true,
        }
      );

      const primary = Array.isArray(data?.primaryRecommendations)
        ? data.primaryRecommendations
        : [];
      const duplicates = Array.isArray(data?.duplicateAttempts)
        ? data.duplicateAttempts
        : [];

      setPrimaryRows(primary);
      setDuplicateRows(duplicates);
      setStats(data?.stats || null);
      setScannedDays(data?.scannedDays ?? days);

      const rec = primary.filter((r) => r.recoverable && r.hasSavedBasket).length;
      toast.success(
        `${rec} payment(s) to recover · ${duplicates.length} duplicate attempt(s) hidden`
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not load orphaned payments.");
      setPrimaryRows([]);
      setDuplicateRows([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  const recoverOne = async (row) => {
    if (!row?.paymentIntentId) return;
    setRecoveringId(row.paymentIntentId);
    try {
      const { data } = await axios.post(
        `${server}/order/recover-stripe-checkout`,
        {
          paymentIntentId: row.paymentIntentId,
          userId: row.userId || undefined,
          basketSourcePaymentIntentId:
            row.basketSourcePaymentIntentId &&
            row.basketSourcePaymentIntentId !== row.paymentIntentId
              ? row.basketSourcePaymentIntentId
              : undefined,
        },
        { withCredentials: true }
      );
      toast.success(data?.message || "Order recovered.");
      setPrimaryRows((prev) =>
        prev.filter((r) => r.paymentIntentId !== row.paymentIntentId)
      );
      setDuplicateRows((prev) =>
        prev.filter((r) => r.paymentIntentId !== row.paymentIntentId)
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || "Recovery failed.");
    } finally {
      setRecoveringId("");
    }
  };

  const recoverAll = async () => {
    const recoverable = primaryRows.filter((r) => r.recoverable && r.hasSavedBasket);
    if (!recoverable.length) {
      toast.info("No recommended payments to recover.");
      return;
    }
    if (
      !window.confirm(
        `Create ${recoverable.length} order(s) from the latest payment in each checkout session? Duplicate payment attempts will be skipped.`
      )
    ) {
      return;
    }
    let ok = 0;
    let fail = 0;
    for (const row of recoverable) {
      setRecoveringId(row.paymentIntentId);
      try {
        await axios.post(
          `${server}/order/recover-stripe-checkout`,
          {
            paymentIntentId: row.paymentIntentId,
            userId: row.userId || undefined,
            basketSourcePaymentIntentId:
              row.basketSourcePaymentIntentId &&
              row.basketSourcePaymentIntentId !== row.paymentIntentId
                ? row.basketSourcePaymentIntentId
                : undefined,
          },
          { withCredentials: true }
        );
        ok += 1;
        setPrimaryRows((prev) =>
          prev.filter((r) => r.paymentIntentId !== row.paymentIntentId)
        );
      } catch {
        fail += 1;
      }
    }
    setRecoveringId("");
    toast.success(`Recovered ${ok} order(s).${fail ? ` ${fail} failed.` : ""}`);
  };

  const syncAllOrdersToStripe = async () => {
    if (
      !window.confirm(
        "Sync all Stripe payments with order numbers and details? This may take a minute."
      )
    ) {
      return;
    }
    setSyncingBulk(true);
    try {
      const { data } = await axios.post(
        `${server}/order/admin-sync-stripe-bulk`,
        {},
        { withCredentials: true, timeout: 300000 }
      );
      setSyncReport({
        synced: Number(data?.synced || 0),
        failed: Number(data?.failed || 0),
        total: Number(data?.total || 0),
        ordersScanned: Number(data?.ordersScanned || 0),
        skippedNonPaymentIntents: Number(data?.skippedNonPaymentIntents || 0),
        syncedItems: Array.isArray(data?.syncedItems) ? data.syncedItems : [],
        diagnostics: data?.diagnostics || null,
        errors: Array.isArray(data?.errors) ? data.errors : [],
      });
      toast.success(
        data?.message || `Updated ${data?.synced ?? 0} Stripe payment(s).`
      );
    } catch (err) {
      setSyncReport(null);
      toast.error(err?.response?.data?.message || "Stripe bulk sync failed.");
    } finally {
      setSyncingBulk(false);
    }
  };

  const auditMissingStripePi = async () => {
    setAuditingMissingPi(true);
    try {
      const { data } = await axios.get(`${server}/order/admin-audit-missing-stripe-pi`, {
        params: { limit: 200 },
        withCredentials: true,
      });
      setMissingPiAudit({
        totalMissing: Number(data?.totalMissing || 0),
        returned: Number(data?.returned || 0),
        rows: Array.isArray(data?.rows) ? data.rows : [],
      });
      toast.success(
        `Found ${Number(data?.totalMissing || 0)} paid Stripe/Klarna order(s) missing valid pi_* id.`
      );
    } catch (err) {
      setMissingPiAudit(null);
      toast.error(
        err?.response?.data?.message || "Failed to audit missing Stripe PI IDs."
      );
    } finally {
      setAuditingMissingPi(false);
    }
  };

  const displayRows = showDuplicates ? [...primaryRows, ...duplicateRows] : primaryRows;
  const recoverableCount = primaryRows.filter(
    (r) => r.recoverable && r.hasSavedBasket
  ).length;

  useEffect(() => {
    loadOrphans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recoveryReasonLabel = (reason) => {
    if (reason === "basket_expired") {
      return "Order not placed — basket expired on server";
    }
    if (reason === "basket_missing") {
      return "Order not placed — no saved basket";
    }
    if (reason === "no_buyer") {
      return "Order not placed — unknown buyer";
    }
    return "Order not placed — manual action needed";
  };

  const stripePaymentUrl = (paymentIntentId) =>
    `https://dashboard.stripe.com/payments/${paymentIntentId}`;

  const renderRow = (row, isDup = false) => {
    const canRecover =
      !isDup && row.recoverable && row.hasSavedBasket && row.isPrimary !== false;
    return (
      <tr
        key={row.paymentIntentId}
        className={isDup ? "text-slate-500 bg-slate-50/80" : "text-slate-700"}
      >
        <td className="px-3 py-2 font-mono text-xs">
          <a
            href={stripePaymentUrl(row.paymentIntentId)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#38513B] hover:underline"
          >
            {row.paymentIntentId}
          </a>
        </td>
        <td className="px-3 py-2">
          <div>{row.buyerEmail || "—"}</div>
          <div className="text-xs text-slate-400">{row.userId || ""}</div>
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          {formatMoney(row.amount, row.currency)}
        </td>
        <td className="px-3 py-2 whitespace-nowrap text-xs">
          {row.createdAt ? new Date(row.createdAt).toLocaleString("en-GB") : "—"}
        </td>
        <td className="px-3 py-2">
          {isDup ? (
            <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
              Duplicate attempt
              {row.duplicateGroupSize > 1 ? ` (${row.duplicateGroupSize})` : ""}
            </span>
          ) : canRecover ? (
            <div className="space-y-0.5">
              <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                Ready to recover
              </span>
              {row.recoveryNote ? (
                <div className="text-xs text-slate-500">{row.recoveryNote}</div>
              ) : null}
            </div>
          ) : (
            <div className="space-y-0.5">
              <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                {recoveryReasonLabel(row.recoveryReason)}
              </span>
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap">
          {canRecover ? (
            <button
              type="button"
              disabled={recoveringId === row.paymentIntentId}
              onClick={() => recoverOne(row)}
              className="rounded-md bg-[#38513B] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2f4232] disabled:opacity-60"
            >
              {recoveringId === row.paymentIntentId ? "…" : "Recover"}
            </button>
          ) : isDup ? (
            <a
              href={stripePaymentUrl(row.paymentIntentId)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#38513B] hover:underline"
            >
              Refund in Stripe
            </a>
          ) : (
            <span className="text-xs text-slate-500">
              <a
                href={stripePaymentUrl(row.paymentIntentId)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#38513B] hover:underline"
              >
                Check in Stripe
              </a>
              <span className="block text-slate-400">Refund only if needed</span>
            </span>
          )}
        </td>
      </tr>
    );
  };

  return (
    <section className="rounded-2xl border border-amber-200 bg-amber-50/40 shadow-sm overflow-hidden">
      <div className="border-b border-amber-200/80 bg-amber-50 px-4 py-3 md:px-5">
        <h2 className="text-lg font-semibold text-slate-800">
          Recover missing Stripe / Klarna orders
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Each row is a <strong>succeeded</strong> Stripe payment with no marketplace order — the
          customer was charged; nothing here auto-refunds. Use <strong>Recover</strong> when a
          basket was saved. If status says order not placed, check Stripe (payment is usually still
          captured) — only refund manually if you cannot fulfil the order. Duplicate checkout
          retries should be refunded in Stripe, not recovered.
        </p>
      </div>

      <div className="p-4 md:p-5 space-y-4 bg-white">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700">Days to scan</span>
            <input
              type="number"
              min={7}
              max={365}
              value={days}
              onChange={(e) => setDays(Number(e.target.value) || 5)}
              className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={loadOrphans}
            disabled={loading}
            className="rounded-lg bg-[#38513B] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2f4232] disabled:opacity-60"
          >
            {loading ? "Scanning…" : "Scan Stripe"}
          </button>
          <button
            type="button"
            onClick={syncAllOrdersToStripe}
            disabled={syncingBulk}
            className="rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-800 hover:bg-indigo-100 disabled:opacity-60"
          >
            {syncingBulk ? "Syncing…" : "Sync all orders to Stripe"}
          </button>
          <button
            type="button"
            onClick={auditMissingStripePi}
            disabled={auditingMissingPi}
            className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-60"
          >
            {auditingMissingPi ? "Auditing…" : "Audit missing PI IDs"}
          </button>
          {recoverableCount > 0 ? (
            <button
              type="button"
              onClick={recoverAll}
              disabled={Boolean(recoveringId) || loading}
              className="rounded-lg border border-[#38513B] bg-white px-4 py-2 text-sm font-semibold text-[#38513B] hover:bg-[#f7faf7] disabled:opacity-60"
            >
              Recover recommended ({recoverableCount})
            </button>
          ) : null}
          {duplicateRows.length > 0 ? (
            <label className="inline-flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={showDuplicates}
                onChange={(e) => setShowDuplicates(e.target.checked)}
              />
              Show {duplicateRows.length} duplicate attempt(s)
            </label>
          ) : null}
        </div>

        {scannedDays != null ? (
          <p className="text-xs text-slate-500">
            Last scan: {scannedDays} days · {stats?.totalSucceededNoOrder ?? "—"} succeeded
            payment(s) · <strong>{recoverableCount}</strong> recommended to recover ·{" "}
            {stats?.duplicateAttempts ?? duplicateRows.length} duplicate(s) hidden
          </p>
        ) : null}

        {syncReport ? (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
            <p className="text-sm font-medium text-indigo-900">
              Stripe sync result: {syncReport.synced} updated, {syncReport.failed} failed
              {syncReport.total ? ` (out of ${syncReport.total} payment intents)` : ""}.
            </p>
            <p className="text-xs text-indigo-800 mt-1">
              Orders scanned in DB: {syncReport.ordersScanned}
              {syncReport.skippedNonPaymentIntents > 0
                ? ` · Skipped non-PI payment ids: ${syncReport.skippedNonPaymentIntents}`
                : ""}
            </p>
            <p className="text-xs text-indigo-700 mt-1">
              Note: this sync scans all eligible marketplace orders in the database, not the
              paginated table shown on this page.
            </p>
            {syncReport.diagnostics ? (
              <p className="text-xs text-indigo-700 mt-1">
                Stripe/Klarna paid with PI:{" "}
                <strong>{Number(syncReport.diagnostics.paidStripeKlarnaWithPi || 0)}</strong>
                {" · "}
                Stripe/Klarna paid missing PI:{" "}
                <strong>{Number(syncReport.diagnostics.paidStripeKlarnaWithoutPi || 0)}</strong>
                {" · "}
                Wallet paid (no PI by design):{" "}
                <strong>{Number(syncReport.diagnostics.paidWalletWithoutPi || 0)}</strong>
              </p>
            ) : null}
            {syncReport.errors?.length ? (
              <div className="mt-2 max-h-36 overflow-auto rounded border border-indigo-100 bg-white">
                <table className="min-w-full text-xs">
                  <thead className="bg-indigo-100 text-indigo-900">
                    <tr>
                      <th className="px-2 py-1 text-left">Payment Intent</th>
                      <th className="px-2 py-1 text-left">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-indigo-50">
                    {syncReport.errors.map((e, i) => (
                      <tr key={`${e.paymentIntentId || "pi"}-${i}`}>
                        <td className="px-2 py-1 font-mono text-[11px]">
                          {e.paymentIntentId || "—"}
                        </td>
                        <td className="px-2 py-1">{e.reason || "sync_failed"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {syncReport.syncedItems?.length ? (
              <div className="mt-2 max-h-36 overflow-auto rounded border border-emerald-100 bg-white">
                <table className="min-w-full text-xs">
                  <thead className="bg-emerald-100 text-emerald-900">
                    <tr>
                      <th className="px-2 py-1 text-left">Updated PI</th>
                      <th className="px-2 py-1 text-left">Mode</th>
                      <th className="px-2 py-1 text-left">Order no(s)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-emerald-50">
                    {syncReport.syncedItems.map((row, i) => (
                      <tr key={`${row.paymentIntentId || "updated"}-${i}`}>
                        <td className="px-2 py-1 font-mono text-[11px]">
                          {row.paymentIntentId || "—"}
                        </td>
                        <td className="px-2 py-1">
                          {row.modeUsed || "unknown"}
                          {row.fallback ? " (fallback)" : ""}
                        </td>
                        <td className="px-2 py-1">{row.orderNumbers || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}

        {missingPiAudit ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm font-medium text-amber-900">
              Missing PI audit: {missingPiAudit.totalMissing} paid Stripe/Klarna order(s) missing
              valid <code>pi_*</code> id.
            </p>
            <p className="text-xs text-amber-800 mt-1">
              Showing {missingPiAudit.returned} most recent row(s). These cannot be synced to Stripe
              metadata until a valid PaymentIntent ID is linked.
            </p>
            {missingPiAudit.rows?.length ? (
              <div className="mt-2 max-h-40 overflow-auto rounded border border-amber-100 bg-white">
                <table className="min-w-full text-xs">
                  <thead className="bg-amber-100 text-amber-900">
                    <tr>
                      <th className="px-2 py-1 text-left">Order</th>
                      <th className="px-2 py-1 text-left">Date</th>
                      <th className="px-2 py-1 text-left">Buyer</th>
                      <th className="px-2 py-1 text-left">Type</th>
                      <th className="px-2 py-1 text-left">Status</th>
                      <th className="px-2 py-1 text-left">paymentInfo.id</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-50">
                    {missingPiAudit.rows.map((row) => (
                      <tr key={row.orderId}>
                        <td className="px-2 py-1">#{row.orderNumber}</td>
                        <td className="px-2 py-1">
                          {row.createdAt
                            ? new Date(row.createdAt).toLocaleString("en-GB")
                            : "—"}
                        </td>
                        <td className="px-2 py-1">
                          {row.buyerName || "—"}
                          {row.buyerEmail ? (
                            <span className="block text-slate-500">{row.buyerEmail}</span>
                          ) : null}
                        </td>
                        <td className="px-2 py-1">{row.paymentType || "—"}</td>
                        <td className="px-2 py-1">{row.paymentStatus || "—"}</td>
                        <td className="px-2 py-1 font-mono text-[11px]">
                          {row.paymentIntentId || "missing"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}

        {displayRows.length === 0 ? (
          <p className="text-sm text-slate-500">
            Click <strong>Scan Stripe</strong> to find paid checkouts without orders.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Payment</th>
                  <th className="px-3 py-2">Buyer</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {primaryRows.map((row) => renderRow(row, false))}
                {showDuplicates
                  ? duplicateRows.map((row) => renderRow(row, true))
                  : null}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminStripeCheckoutRecovery;
